import React, { useState, useRef, useEffect } from "react";
import { useTasks } from "../context/TaskContext";
import { useProjects } from "../context/ProjectContext";
import { toast } from "sonner";
import Swal from "sweetalert2";
import { formatDateLegible } from "../utils/dateUtils";
import { triggerTaskCompletedConfetti } from "../utils/confettiUtils";
import { TaskEditModal } from "./tasks/TaskEditModal";

export const TaskCard = ({ task }) => {
    const { deleteTask, updateTask } = useTasks();
    const { projects } = useProjects();

    // 1. Buscamos el proyecto asignado a esta tarea
    const currentProject = projects.find(p => p.id === task.project_id);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    // Estado y referencias para mantener presionado en móvil (Hold to Complete/Undo)
    const [holdProgress, setHoldProgress] = useState(0);
    const holdTimerRef = useRef(null);
    const holdStartTimeRef = useRef(null);

    const cancelHold = () => {
        if (holdTimerRef.current) {
            clearInterval(holdTimerRef.current);
            holdTimerRef.current = null;
        }
        setHoldProgress(0);
    };

    const startHold = (e) => {
        // Activar únicamente en pantallas móviles (< 768px)
        if (typeof window === "undefined" || window.innerWidth >= 768) return;

        cancelHold();

        holdStartTimeRef.current = Date.now();
        const HOLD_DURATION = 550; // milisegundos de presión requeridos

        if (typeof window !== "undefined" && "vibrate" in navigator) {
            navigator.vibrate(10);
        }

        holdTimerRef.current = setInterval(() => {
            const elapsed = Date.now() - holdStartTimeRef.current;
            const progress = Math.min(100, (elapsed / HOLD_DURATION) * 100);
            setHoldProgress(progress);

            if (progress >= 100) {
                cancelHold();
                handleToggleDone(e);
            }
        }, 16);
    };

    useEffect(() => {
        return () => cancelHold();
    }, []);

    // Formatters para hora y fecha de recordatorio
    const formatTimeLegible = (timeStr) => {
        if (!timeStr) return "";
        return timeStr.slice(0, 5); // Formato HH:MM
    };

    const formatReminderLegible = () => {
        if (!task.reminder_type || task.reminder_type === "none") return null;

        if (task.reminder_type === "at_time") {
            return task.due_time ? formatTimeLegible(task.due_time) : "A la hora";
        }

        if (task.reminder_type === "custom" && task.reminder_date) {
            const [datePart, timePart] = task.reminder_date.replace(" ", "T").split("T");
            const dateFormatted = formatDateLegible(datePart);
            const timeFormatted = timePart ? formatTimeLegible(timePart) : "";
            return `${dateFormatted}${timeFormatted ? `, ${timeFormatted}` : ""}`;
        }

        return "Activo";
    };

    // 2. Estilos inline dinámicos aplicando el color HEX del proyecto
    const getDynamicCardStyles = () => {
        if (task.done) {
            return {
                style: {
                    borderLeftColor: "#64748b",
                    borderColor: "#e2e8f0",
                    backgroundColor: "#f8fafc"
                },
                dotClass: "border-emerald-500 bg-emerald-500 text-white shadow-emerald-200 hover:bg-emerald-600",
                badgeClass: "bg-slate-100 text-slate-700 border-slate-200"
            };
        }

        const projectColor = currentProject?.color || "#f59e0b";

        return {
            style: {
                borderLeftColor: projectColor,
                borderColor: `${projectColor}33`,
                backgroundColor: `${projectColor}0D`
            },
            dotClass: "border-slate-300 bg-white text-slate-600 hover:border-emerald-500 hover:text-emerald-600",
            badgeClass: "bg-amber-50 text-amber-700 border-amber-200"
        };
    };

    const cardStyles = getDynamicCardStyles();

    const priorityFlags = {
        1: "text-red-500",
        2: "text-orange-500",
        3: "text-blue-500",
        4: "text-slate-400"
    };

    const handleDelete = () => {
        Swal.fire({
            title: "¿Eliminar tarea?",
            html: `¿Estás seguro de que deseas eliminar <b class="text-red-500">"${task.name}"</b>?<br/><span class="text-sm text-gray-500">Esta acción no se puede deshacer.</span>`,
            icon: "warning",
            iconColor: "#ef4444",
            showCancelButton: true,
            confirmButtonColor: "#dc2626",
            cancelButtonColor: "#6b7280",
            confirmButtonText: '<i class="fas fa-trash-alt mr-2"></i> Sí, eliminar',
            cancelButtonText: "Cancelar",
            reverseButtons: true,
            customClass: {
                popup: "rounded-2xl shadow-2xl border border-gray-100 dark:border-slate-800 dark:bg-slate-900 dark:text-white p-6",
                title: "text-xl font-bold text-gray-800 dark:text-white",
                confirmButton: "px-5 py-2.5 rounded-xl font-medium shadow-md transition-transform active:scale-95",
                cancelButton: "px-5 py-2.5 rounded-xl font-medium shadow-sm transition-transform active:scale-95"
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deleteTask(task.id);
                    toast.success("La tarea ha sido eliminada correctamente.");
                } catch (error) {
                    toast.error("Error al eliminar la tarea.");
                }
            }
        });
    };

    const handleToggleDone = (e) => {
        const nextState = !task.done;

        let x = 0.5;
        let y = 0.6;
        if (e && e.clientX && e.clientY) {
            x = e.clientX / window.innerWidth;
            y = e.clientY / window.innerHeight;
        }

        if (nextState) {
            // 1. Vibración háptica en móvil
            if (typeof window !== "undefined" && "vibrate" in navigator) {
                navigator.vibrate([20, 35, 20]);
            }

            // 2. Disparar confeti de celebración
            triggerTaskCompletedConfetti({ x, y });

            // 3. Activar micro-animación en el checkbox y tarjeta
            setIsAnimating(true);
            setTimeout(() => setIsAnimating(false), 500);

            // 4. Toast flotante interactivo con opción "Deshacer"
            toast.success("¡Tarea completada! 🎉", {
                description: task.name,
                action: {
                    label: "Deshacer",
                    onClick: () => {
                        updateTask(task.id, { done: false });
                        if (typeof window !== "undefined" && "vibrate" in navigator) {
                            navigator.vibrate(15);
                        }
                        toast.info("Tarea restaurada a pendiente", {
                            description: task.name
                        });
                    }
                },
                duration: 4500
            });
        } else {
            if (typeof window !== "undefined" && "vibrate" in navigator) {
                navigator.vibrate(15);
            }
            toast.info("Tarea marcada como pendiente", {
                description: task.name
            });
        }

        updateTask(task.id, { done: nextState });
    };

    const reminderLabel = formatReminderLegible();

    const handleCardDoubleClick = (e) => {
        // En pantallas de escritorio (>= 768px), 2 clics (doble clic) conmutan la tarea
        if (typeof window !== "undefined" && window.innerWidth >= 768) {
            handleToggleDone(e);
        }
    };

    return (
        <>
            {/* Tarjeta de Tarea */}
            <div
                style={cardStyles.style}
                onTouchStart={startHold}
                onTouchEnd={cancelHold}
                onTouchCancel={cancelHold}
                onMouseDown={(e) => { if (typeof window !== "undefined" && window.innerWidth < 768) startHold(e); }}
                onMouseUp={cancelHold}
                onMouseLeave={cancelHold}
                onDoubleClick={handleCardDoubleClick}
                className={`rounded-2xl p-4 border-2 border-l-8 shadow-sm hover:shadow-md transition-all duration-200 flex items-start space-x-3.5 group relative cursor-pointer select-none overflow-hidden ${isAnimating ? "animate-task-completed" : ""}`}
                title={typeof window !== "undefined" && window.innerWidth < 768 ? (task.done ? "Mantén presionado para deshacer" : "Mantén presionado para completar") : "Haz doble clic para marcar/desmarcar"}
            >
                {/* Capa de Relleno de Carga al Mantener Presionado en Móvil */}
                {holdProgress > 0 && (
                    <div
                        className={`absolute inset-0 pointer-events-none transition-all duration-75 ease-out rounded-2xl border-r-2 z-10 ${task.done
                            ? "bg-amber-400/15 border-amber-400/60 shadow-inner"
                            : "bg-emerald-400/15 border-emerald-400/60 shadow-inner"
                            }`}
                        style={{ width: `${holdProgress}%` }}
                    />
                )}
                {/* Checkbox Circular */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleToggleDone(e);
                    }}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm transition-all duration-200 transform active:scale-90 ${cardStyles.dotClass} ${isAnimating ? "animate-check-pop bg-emerald-500 border-emerald-500 text-white" : ""}`}
                    title={task.done ? "Marcar como pendiente" : "Marcar como completada"}
                >
                    <i className={`fas fa-check text-[10px] ${task.done || isAnimating ? "opacity-100" : "opacity-0 group-hover:opacity-100 transition-opacity"}`}></i>
                </button>

                {/* Contenedor de Texto Central */}
                <div className="flex-1 min-w-0">
                    <h1 className={`text-sm font-semibold text-slate-800 tracking-wide break-words ${task.done ? 'line-through text-slate-400 font-normal' : ''}`}>
                        {task.name}
                    </h1>
                    {task.description && (
                        <p className={`text-xs text-slate-600 mt-1 leading-relaxed break-words ${task.done ? 'line-through opacity-50' : ''}`}>
                            {task.description}
                        </p>
                    )}

                    {/* Bloque Inferior de Etiquetas (Centrado en móvil, alineado a la izquierda en pantallas más grandes) */}
                    <div className="mt-3 flex flex-wrap gap-2 items-center justify-center sm:justify-start text-[11px] font-medium">
                        <span className={`px-2 py-0.5 border rounded-md text-[10px] font-bold uppercase tracking-wide ${cardStyles.badgeClass}`}>
                            {task.done ? "Completada" : "Pendiente"}
                        </span>

                        {/* Etiqueta del Proyecto */}
                        {currentProject && (
                            <span
                                className="flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border"
                                style={{
                                    borderColor: `${currentProject.color}40`,
                                    color: currentProject.color,
                                    backgroundColor: `${currentProject.color}15`
                                }}
                            >
                                <span className="mr-1">{currentProject.icon}</span>
                                {currentProject.name}
                            </span>
                        )}

                        {/* Fecha + Hora Límite */}
                        {task.due_date && (
                            <span className="flex items-center px-2 py-0.5 bg-slate-100/80 border border-slate-200 rounded-md text-slate-600">
                                <i className="far fa-calendar-alt mr-1.5 text-slate-500"></i>
                                {formatDateLegible(task.due_date)}
                                {task.due_time && (
                                    <span className="ml-1 text-slate-500 font-normal">
                                        , {formatTimeLegible(task.due_time)}
                                    </span>
                                )}
                            </span>
                        )}

                        {/* Recordatorio (Campanita) */}
                        {reminderLabel && (
                            <span className="flex items-center px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-md text-blue-700 font-medium">
                                <i className="far fa-bell mr-1.5 text-blue-500 animate-pulse"></i>
                                {reminderLabel}
                            </span>
                        )}

                        {/* Prioridad */}
                        <span className="flex items-center px-2 py-0.5 bg-white border border-slate-200 rounded-md text-slate-600">
                            <i className={`fas fa-flag mr-1.5 ${priorityFlags[task.priority] || "text-slate-400"}`}></i>
                            P{task.priority}
                        </span>
                    </div>
                </div>

                {/* Menú de Acciones (Pegado a la esquina superior derecha en móvil: top-1.5 right-1.5) */}
                <div className="flex items-center space-x-0.5 md:space-x-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-150 absolute top-1.5 right-1.5 md:top-3 md:right-3 bg-white/80 md:bg-white/90 backdrop-blur-sm px-1 md:pl-2 py-0.5 rounded-lg shadow-xs">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditModalOpen(true);
                        }}
                        className="p-1 md:p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Editar tarea"
                    >
                        <i className="far fa-edit text-xs md:text-sm"></i>
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDelete();
                        }}
                        className="p-1 md:p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Eliminar tarea"
                    >
                        <i className="far fa-trash-alt text-xs md:text-sm"></i>
                    </button>
                </div>
            </div>

            {/* Modal de edición aislado */}
            {isEditModalOpen && (
                <TaskEditModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    task={task}
                />
            )}
        </>
    );
};